import React, { useState, useEffect } from 'react';
import { useLicense } from '../contexts/LicenseContext';
import apiService from '../services/apiService';
import './LicenseInfo.css';

const LicenseInfo = () => {
  const { isLicenseValid, licenseKey, userEmail } = useLicense();
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [validationStatus, setValidationStatus] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState('');
  const [validationMessage, setValidationMessage] = useState('');
  
  // Update manager state
  const [updateStatus, setUpdateStatus] = useState(null);
  const [updateProgress, setUpdateProgress] = useState(null);
  const [appVersion, setAppVersion] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [showUpdatePanel, setShowUpdatePanel] = useState(false);

  useEffect(() => {
    const updateLicenseInfo = async () => {
      try {
        if (licenseKey && isLicenseValid) {
          const result = await apiService.getLicenseStatus(licenseKey);
          
          if (result.found) {
            console.log('📋 License Info received:', result.license);
            console.log('📋 Active Systems fields:', {
              activeDevices: result.license.activeDevices,
              activeSystems: result.license.activeSystems,
              registeredSystemsCount: result.license.registeredSystemsCount,
              systems: result.license.systems,
              systemsLength: Array.isArray(result.license.systems) ? result.license.systems.length : 'not an array',
              allKeys: Object.keys(result.license)
            });
            console.log('📋 Max Systems fields:', {
              maxSystems: result.license.maxSystems,
              maxSystemsType: typeof result.license.maxSystems,
              maxDevices: result.license.maxDevices,
              maxDevicesType: typeof result.license.maxDevices,
              activatedAt: result.license.activatedAt,
              createdAt: result.license.createdAt
            });
            
            // Preserve API response fields - API may return activeSystems, registeredSystemsCount, activeDevices, systems, or SystemRegistrations (PG backend)
            const regs = result.license.SystemRegistrations || result.license.systems || result.license.activeDevices || [];
            const regsArray = Array.isArray(regs) ? regs : [];
            const countFromArrays = regsArray.length;
            const activeFromApi = result.license.hasOwnProperty('activeSystems') ? result.license.activeSystems : undefined;
            const registeredFromApi = result.license.hasOwnProperty('registeredSystemsCount') ? result.license.registeredSystemsCount : undefined;
            // When API sends 0 but we have registered systems in arrays, use array count so "Active system" isn't 0/2 when user has 1 active
            const activeCount = typeof activeFromApi === 'number' && activeFromApi > 0
              ? activeFromApi
              : (typeof registeredFromApi === 'number' && registeredFromApi > 0 ? registeredFromApi : countFromArrays);
            const licenseWithDefaults = {
              ...result.license,
              activeSystems: activeCount,
              registeredSystemsCount: typeof registeredFromApi === 'number' ? registeredFromApi : activeCount,
              activeDevices: result.license.activeDevices !== undefined ? result.license.activeDevices : regsArray,
              systems: result.license.systems !== undefined ? result.license.systems : regsArray
            };
            
            // Debug: Log the final values to help diagnose
            console.log('📊 Final licenseWithDefaults:', {
              activeSystems: licenseWithDefaults.activeSystems,
              activeSystemsType: typeof licenseWithDefaults.activeSystems,
              registeredSystemsCount: licenseWithDefaults.registeredSystemsCount,
              registeredSystemsCountType: typeof licenseWithDefaults.registeredSystemsCount,
              systemsArrayLength: Array.isArray(licenseWithDefaults.systems) ? licenseWithDefaults.systems.length : 'not array',
              activeDevicesArrayLength: Array.isArray(licenseWithDefaults.activeDevices) ? licenseWithDefaults.activeDevices.length : 'not array'
            });
            
            console.log('📋 License with defaults:', licenseWithDefaults);
            setLicenseInfo(licenseWithDefaults);
            const expiryDate = new Date(result.license.expiryDate);
            const now = new Date();
            const timeRemainingMs = expiryDate.getTime() - now.getTime();
            
            // Check if it's a lifetime license
            const yearsDifference = expiryDate.getFullYear() - now.getFullYear();
            if (yearsDifference >= 999 || result.license.plan === 'lifetime') {
              setTimeRemaining('Lifetime');
            } else if (timeRemainingMs <= 0) {
              setTimeRemaining('Expired');
            } else {
              const days = Math.floor(timeRemainingMs / (1000 * 60 * 60 * 24));
              const hours = Math.floor((timeRemainingMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
              setTimeRemaining(`${days} days, ${hours} hours`);
            }
            setError('');
          } else {
            setLicenseInfo(null);
            setError('License not found');
          }
        } else {
          setLicenseInfo(null);
          setError('No license key stored');
        }

        // Set validation status based on license validity
        setValidationStatus({
          lastValidation: new Date().toISOString(),
          validUntil: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString()
        });
      } catch (error) {
        console.error('Error updating license info:', error);
        setError('Failed to load license information');
      } finally {
        setIsLoading(false);
      }
    };

    updateLicenseInfo();
    const interval = setInterval(updateLicenseInfo, 60000); // Update every minute
    
    // Get current app version
    if (window.electronAPI) {
      window.electronAPI.getAppVersion().then(version => {
        setAppVersion(version);
      });
    }

    // Listen for update status
    if (window.electronAPI) {
      window.electronAPI.onUpdateStatus((event, status) => {
        console.log('Update status received:', status);
        setUpdateStatus(status);
        
        // Show update panel for important statuses
        if (status.status === 'available' || status.status === 'downloaded') {
          setShowUpdatePanel(true);
        }
      });

      window.electronAPI.onUpdateProgress((event, progress) => {
        console.log('Update progress received:', progress);
        setUpdateProgress(progress);
      });
    }
    
    return () => clearInterval(interval);
  }, []);

  const handleManualValidation = async () => {
    if (!licenseInfo) return;
    
    setIsValidating(true);
    setValidationMessage('');
    
    try {
      const result = await apiService.validateLicense(licenseInfo.licenseKey);
      
      if (result.isValid) {
        setValidationMessage(`✅ License validated successfully`);
        // Refresh license info
        const statusResult = await apiService.getLicenseStatus(licenseInfo.licenseKey);
        if (statusResult.found) {
          setLicenseInfo(statusResult.license);
        }
        // Update validation status
        setValidationStatus({
          lastValidation: new Date().toISOString(),
          validUntil: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString()
        });
      } else {
        setValidationMessage(`❌ ${result.reason || 'License validation failed'}`);
      }
    } catch (error) {
      setValidationMessage(`❌ Validation failed: ${error.message}`);
    } finally {
      setIsValidating(false);
    }
  };

  // Update manager handlers
  const handleCheckForUpdates = async () => {
    if (!window.electronAPI) return;
    
    setIsChecking(true);
    try {
      const result = await window.electronAPI.checkForUpdates();
      console.log('Update check result:', result);
    } catch (error) {
      console.error('Error checking for updates:', error);
    } finally {
      setIsChecking(false);
    }
  };

  const handleDownloadUpdate = async () => {
    if (!window.electronAPI) return;
    
    try {
      const result = await window.electronAPI.downloadUpdate();
      console.log('Download result:', result);
    } catch (error) {
      console.error('Error downloading update:', error);
    }
  };

  const handleInstallUpdate = async () => {
    if (!window.electronAPI) return;
    
    try {
      const result = await window.electronAPI.installUpdate();
      console.log('Install result:', result);
    } catch (error) {
      console.error('Error installing update:', error);
    }
  };

  // Get max systems count safely (ensure it's a number, not a date)
  const getMaxSystems = () => {
    if (!licenseInfo) {
      console.log('📊 getMaxSystems: No licenseInfo');
      return 15; // Default fallback
    }
    
    // Debug: Log all available fields
    console.log('📊 getMaxSystems - licenseInfo fields:', {
      maxSystems: licenseInfo.maxSystems,
      maxSystemsType: typeof licenseInfo.maxSystems,
      maxDevices: licenseInfo.maxDevices,
      maxDevicesType: typeof licenseInfo.maxDevices,
      allKeys: Object.keys(licenseInfo)
    });
    
    // Helper function to check if a value is a date string
    const isDateString = (value) => {
      if (typeof value !== 'string') return false;
      // Check for ISO date format (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss)
      const datePattern = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?)?$/;
      // Check for other date formats
      const hasDateIndicators = value.includes('T') || (value.includes('-') && value.match(/\d{4}-\d{2}-\d{2}/)) || value.includes('/');
      return datePattern.test(value) || hasDateIndicators;
    };
    
    // Priority order: Check numeric maxSystems/maxDevices fields first
    // 1. maxSystems (primary field from API) - must be a number, NOT a date
    if (licenseInfo.hasOwnProperty('maxSystems')) {
      const maxSys = licenseInfo.maxSystems;
      // Check if it's a number (not a date string)
      if (typeof maxSys === 'number' && !isNaN(maxSys) && isFinite(maxSys) && maxSys > 0 && maxSys < 10000) {
        // Additional check: reasonable max systems should be between 1 and 10000
        console.log('📊 Using maxSystems (number):', maxSys);
        return maxSys;
      }
      // If it's a string that looks like a date, skip it completely
      if (isDateString(maxSys)) {
        console.warn('⚠️ maxSystems appears to be a date string, skipping:', maxSys);
        // Don't try to parse dates - skip to next option
      } else if (typeof maxSys === 'string') {
        // Only try to parse if it's NOT a date string
        const parsed = parseInt(maxSys, 10);
        if (!isNaN(parsed) && isFinite(parsed) && parsed > 0 && parsed < 10000) {
          console.log('📊 Parsed maxSystems from string (non-date):', parsed);
          return parsed;
        }
      }
    }
    
    // 2. maxDevices (alternate field from API)
    if (licenseInfo.hasOwnProperty('maxDevices')) {
      const maxDev = licenseInfo.maxDevices;
      if (typeof maxDev === 'number' && !isNaN(maxDev) && isFinite(maxDev) && maxDev > 0 && maxDev < 10000) {
        console.log('📊 Using maxDevices (number):', maxDev);
        return maxDev;
      }
      // Don't try to parse if it looks like a date
      if (!isDateString(maxDev) && typeof maxDev === 'string') {
        const parsed = parseInt(maxDev, 10);
        if (!isNaN(parsed) && isFinite(parsed) && parsed > 0 && parsed < 10000) {
          console.log('📊 Parsed maxDevices from string:', parsed);
          return parsed;
        }
      }
    }
    
    // 3. Check other potential fields (seats, maxSeats, deviceLimit, etc.)
    const alternativeFields = ['seats', 'maxSeats', 'deviceLimit', 'maxDevicesPerUser'];
    for (const field of alternativeFields) {
      if (licenseInfo.hasOwnProperty(field)) {
        const value = licenseInfo[field];
        if (typeof value === 'number' && !isNaN(value) && isFinite(value) && value > 0 && value < 10000) {
          console.log(`📊 Using ${field} (number):`, value);
          return value;
        }
      }
    }
    
    // 4. Default fallback values based on plan
    if (licenseInfo.plan) {
      const planLower = String(licenseInfo.plan).toLowerCase();
      if (planLower.includes('lifetime') || planLower.includes('unlimited')) {
        console.log('📊 Using default maxSystems for lifetime/unlimited plan: 999');
        return 999;
      }
      if (planLower.includes('enterprise')) {
        console.log('📊 Using default maxSystems for enterprise plan: 50');
        return 50;
      }
      if (planLower.includes('premium')) {
        console.log('📊 Using default maxSystems for premium plan: 15');
        return 15;
      }
    }
    
    // 5. Default to 15 if nothing else works (common default)
    console.warn('⚠️ No valid maxSystems found, using default: 15');
    return 15;
  };

  // Compute current active systems
  const getCurrentSystems = () => {
    if (!licenseInfo) {
      console.log('📊 getCurrentSystems: No licenseInfo');
      return 0;
    }
    
    // Debug: Log all available fields
    console.log('📊 getCurrentSystems - licenseInfo fields:', {
      activeSystems: licenseInfo.activeSystems,
      activeSystemsType: typeof licenseInfo.activeSystems,
      registeredSystemsCount: licenseInfo.registeredSystemsCount,
      systems: licenseInfo.systems,
      systemsLength: Array.isArray(licenseInfo.systems) ? licenseInfo.systems.length : 'N/A',
      activeDevicesLength: Array.isArray(licenseInfo.activeDevices) ? licenseInfo.activeDevices.length : 'N/A',
      SystemRegistrationsLength: Array.isArray(licenseInfo.SystemRegistrations) ? licenseInfo.SystemRegistrations.length : 'N/A',
      allKeys: Object.keys(licenseInfo)
    });
    
    // Priority order: Check numeric count fields first (API returns these)
    // 1. activeSystems (primary field from API) - but when it's 0 and we have registered systems in arrays, use array count
    if (licenseInfo.hasOwnProperty('activeSystems') && typeof licenseInfo.activeSystems === 'number') {
      const fromApi = licenseInfo.activeSystems;
      if (fromApi > 0) {
        console.log('📊 Using activeSystems count:', fromApi);
        return fromApi;
      }
      // API says 0 but user may have active systems in arrays (e.g. PG returns SystemRegistrations)
      const arr = licenseInfo.SystemRegistrations || licenseInfo.systems || licenseInfo.activeDevices;
      if (Array.isArray(arr) && arr.length > 0) {
        const count = arr.filter(s => s && (s.isActive !== false)).length;
        const use = count > 0 ? count : arr.length;
        console.log('📊 activeSystems was 0 but systems array has entries, using count:', use);
        return use;
      }
      return fromApi;
    }
    
    // 2. registeredSystemsCount (alternate field from API)
    if (licenseInfo.hasOwnProperty('registeredSystemsCount') && typeof licenseInfo.registeredSystemsCount === 'number') {
      console.log('📊 Using registeredSystemsCount:', licenseInfo.registeredSystemsCount);
      return licenseInfo.registeredSystemsCount;
    }
    
    // 3. Other numeric fields (fallback)
    if (typeof licenseInfo.systemsRegistered === 'number') {
      console.log('📊 Using systemsRegistered:', licenseInfo.systemsRegistered);
      return licenseInfo.systemsRegistered;
    }
    if (typeof licenseInfo.active_devices === 'number') {
      console.log('📊 Using active_devices:', licenseInfo.active_devices);
      return licenseInfo.active_devices;
    }
    if (typeof licenseInfo.activeDevices === 'number') {
      console.log('📊 Using activeDevices (number):', licenseInfo.activeDevices);
      return licenseInfo.activeDevices;
    }
    
    // 4. Count from arrays if numeric fields not available
    if (Array.isArray(licenseInfo.systems)) {
      // If systems have isActive flag, count only active ones
      if (licenseInfo.systems.length > 0 && typeof licenseInfo.systems[0] === 'object' && 'isActive' in licenseInfo.systems[0]) {
        const count = licenseInfo.systems.filter(s => s && s.isActive !== false).length;
        console.log('📊 Using systems array (with isActive filter):', count);
        return count;
      }
      console.log('📊 Using systems array length:', licenseInfo.systems.length);
      return licenseInfo.systems.length;
    }
    
    if (Array.isArray(licenseInfo.activeDevices)) {
      const count = licenseInfo.activeDevices.length;
      console.log('📊 Using activeDevices array length:', count);
      return count;
    }
    
    if (Array.isArray(licenseInfo.devices)) {
      console.log('📊 Using devices array length:', licenseInfo.devices.length);
      return licenseInfo.devices.length;
    }
    
    console.warn('⚠️ No active systems count found in licenseInfo. Available keys:', Object.keys(licenseInfo));
    return 0;
  };

  const getStatusIcon = () => {
    if (!updateStatus) return '🔄';
    
    switch (updateStatus.status) {
      case 'checking': return '🔄';
      case 'available': return '📦';
      case 'downloading': return '📥';
      case 'downloaded': return '✅';
      case 'error': return '❌';
      case 'not-available': return '✅';
      default: return '🔄';
    }
  };

  const getUpdateStatusColor = () => {
    if (!updateStatus) return '#6c757d';
    
    switch (updateStatus.status) {
      case 'checking': return '#17a2b8';
      case 'available': return '#28a745';
      case 'downloading': return '#ffc107';
      case 'downloaded': return '#28a745';
      case 'error': return '#dc3545';
      case 'not-available': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const getProgressPercentage = () => {
    if (!updateProgress) return 0;
    return Math.round((updateProgress.transferred / updateProgress.total) * 100);
  };

  const formatTimeUntilNextValidation = (milliseconds) => {
    if (!milliseconds || milliseconds <= 0) return 'Now';
    
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      const remainingHours = hours % 24;
      return `${days} day${days > 1 ? 's' : ''}${remainingHours > 0 ? `, ${remainingHours} hour${remainingHours > 1 ? 's' : ''}` : ''}`;
    } else {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    }
  };

  if (isLoading) {
    return (
      <div className="license-info-horizontal-container">
        <div className="license-loading">
          <div className="loading-spinner"></div>
          <p>Loading license information...</p>
        </div>
      </div>
    );
  }

  if (!licenseInfo) {
    return (
      <div className="license-info-horizontal-container">
        <div className="license-error">
          <h2>No License Information</h2>
          <p>{error || 'Please enter a valid license key to view license information.'}</p>
          <p style={{ marginTop: '10px' }}>
            <a href="https://fiddyscript.com" target="_blank" rel="noopener noreferrer">
              Upgrade or Buy a license
            </a>
          </p>
        </div>
      </div>
    );
  }

  const getStatusColor = () => {
    if (!licenseInfo.isActive) return '#dc3545';
    
    // Lifetime licenses are always active
    if (isLifetimeLicense()) return '#28a745';
    
    const expiryDate = new Date(licenseInfo.expiryDate);
    const now = new Date();
    const timeRemaining = expiryDate.getTime() - now.getTime();
    if (timeRemaining <= 0) return '#dc3545';
    if (timeRemaining < 7 * 24 * 60 * 60 * 1000) return '#ffc107';
    return '#28a745';
  };

  const getStatusText = () => {
    if (!licenseInfo.isActive) return 'Inactive';
    
    // Lifetime licenses are always active
    if (isLifetimeLicense()) return 'Lifetime';
    
    const expiryDate = new Date(licenseInfo.expiryDate);
    const now = new Date();
    const timeRemaining = expiryDate.getTime() - now.getTime();
    if (timeRemaining <= 0) return 'Expired';
    if (timeRemaining < 7 * 24 * 60 * 60 * 1000) return 'Expiring Soon';
    return 'Active';
  };

  

  // Check if a license is a lifetime license
  const isLifetimeLicense = () => {
    if (!licenseInfo || !licenseInfo.expiryDate) return false;
    
    // Check if it's marked as lifetime plan
    if (licenseInfo.plan === 'lifetime') return true;
    
    // Check if expiry date is very far in the future (999+ years)
    const expiryDate = new Date(licenseInfo.expiryDate);
    const now = new Date();
    const yearsDifference = expiryDate.getFullYear() - now.getFullYear();
    return yearsDifference >= 999;
  };

  return (
    <div className="license-info-horizontal-container">
      <div className="license-horizontal-header">
        <h2>License Information</h2>
        <div className="header-actions">
          <div className="license-status-pill" style={{ backgroundColor: getStatusColor() }}>
            {getStatusText()}
          </div>
          <div className="update-section">
            <div className="version-info">
              <span className="version-label">Version:</span>
              <span className="version-number">{appVersion}</span>
            </div>
            <button 
              className="check-updates-btn"
              onClick={handleCheckForUpdates}
              disabled={isChecking}
            >
              {isChecking ? 'Checking...' : 'Check for Updates'}
            </button>
            {updateStatus && (
              <div 
                className="status-indicator"
                style={{ backgroundColor: getUpdateStatusColor() }}
                title={updateStatus.message}
              >
                {getStatusIcon()}
              </div>
            )}
          </div>
        </div>
      </div>
      <div style={{ textAlign: 'right', marginBottom: '10px' }}>
        <a href="https://fiddyscript.com" target="_blank" rel="noopener noreferrer">
          Upgrade or Buy a license
        </a>
      </div>
      
      {validationMessage && (
        <div className="validation-message">
          {validationMessage}
        </div>
      )}

      {/* Update Status Panel */}
      {showUpdatePanel && updateStatus && (
        <div className="update-panel">
          <div className="update-panel-header">
            <span className="update-icon">{getStatusIcon()}</span>
            <h3>Update Available</h3>
            <button 
              className="close-update-panel"
              onClick={() => setShowUpdatePanel(false)}
            >
              ×
            </button>
          </div>
          
          <div className="update-panel-content">
            <p>{updateStatus.message}</p>
            
            {updateStatus.status === 'available' && (
              <button 
                className="download-update-btn"
                onClick={handleDownloadUpdate}
              >
                Download Update
              </button>
            )}
            
            {updateStatus.status === 'downloaded' && (
              <button 
                className="install-update-btn"
                onClick={handleInstallUpdate}
              >
                Install & Restart
              </button>
            )}
            
            {updateProgress && updateStatus.status === 'downloading' && (
              <div className="update-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${getProgressPercentage()}%` }}
                  ></div>
                </div>
                <span className="progress-text">
                  {getProgressPercentage()}% ({Math.round(updateProgress.transferred / 1024 / 1024)}MB / {Math.round(updateProgress.total / 1024 / 1024)}MB)
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      
      {isLifetimeLicense() && (
        <div className="lifetime-badge">
          <span className="lifetime-text">🌟 LIFETIME LICENSE</span>
        </div>
      )}
      
      <div className="license-horizontal-sections">
        <div className="license-horizontal-card">
          <h3>Customer</h3>
          <div className="detail-row"><span className="detail-label">Name:</span><span className="detail-value">{licenseInfo.customerName}</span></div>
          <div className="detail-row"><span className="detail-label">Email:</span><span className="detail-value">{licenseInfo.customerEmail}</span></div>
        </div>
        <div className="license-horizontal-card">
          <h3>Status</h3>
          <div className="detail-row"><span className="detail-label">Status:</span><span className="detail-value" style={{ color: getStatusColor() }}>{getStatusText()}</span></div>
          <div className="detail-row"><span className="detail-label">Expiry Date:</span><span className="detail-value">{isLifetimeLicense() ? 'Lifetime' : new Date(licenseInfo.expiryDate).toLocaleDateString()}</span></div>
          <div className="detail-row"><span className="detail-label">Time Remaining:</span><span className="detail-value time-remaining">{isLifetimeLicense() ? 'N/A (Lifetime)' : timeRemaining}</span></div>
        </div>
        <div className="license-horizontal-card">
          <h3>System</h3>
          <div className="detail-row"><span className="detail-label">Max Systems:</span><span className="detail-value">{getMaxSystems()}</span></div>
          <div className="detail-row"><span className="detail-label">Active Systems:</span><span className="detail-value">{getCurrentSystems()} / {getMaxSystems()}</span></div>
        </div>
        {validationStatus && (
          <div className="license-horizontal-card">
            <h3>Validation Status</h3>
            <div className="detail-row">
              <span className="detail-label">Last Validation:</span>
              <span className="detail-value">
                {validationStatus.lastValidation ? validationStatus.lastValidation.toLocaleString() : 'Never'}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Next Validation:</span>
              <span className="detail-value">
                {validationStatus.nextValidation ? validationStatus.nextValidation.toLocaleString() : 'Now'}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Time Until Next:</span>
              <span className="detail-value">
                {formatTimeUntilNextValidation(validationStatus.timeUntilNextValidation)}
              </span>
            </div>
            <button 
              className="manual-validation-btn"
              onClick={handleManualValidation}
              disabled={isValidating}
            >
              {isValidating ? 'Validating...' : 'Validate Now'}
            </button>
          </div>
        )}
        {licenseInfo.systems && licenseInfo.systems.length > 0 && (
          <div className="license-horizontal-card">
            <h3>Registered Systems</h3>
            <div className="systems-list-horizontal">
              {licenseInfo.systems.map((system, index) => (
                <div key={index} className="system-item-horizontal">
                  <div className="system-id-horizontal">{system.systemId}</div>
                  <div className="system-dates-horizontal">
                    <div>Activated: {new Date(system.activatedAt).toLocaleDateString()}</div>
                    <div>Last Seen: {new Date(system.lastSeen).toLocaleDateString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LicenseInfo; 